import { Box, FieldSelect, FlexBox } from '@dixa/lib-shared-habla';
import { ParentRef } from 'lib-knowledge-management-common/models/types';

import CategoryTreeInput from './categoryTreeInput/index';

/** A `null` clears the field; an absent key leaves it as it is. */
export type DraftFilingTarget = {
  collectionId?: string | null;
  categoryId?: string | null;
};

export type DraftFilingProps = {
  /**
   * The collections this draft may be filed into. Passed in rather than
   * queried: which collections a user may file into is not the same question in
   * every app — the review queue lists the ones the management API returns, the
   * agent interface only the ones the agent has access to.
   */
  collections: ReadonlyArray<{ id: string; name: string }>;
  /** Whether `collections` is still being read. */
  collectionsLoading?: boolean;
  /** Whether more collections can be loaded, as the list scrolls to its end. */
  hasMoreCollections?: boolean;
  onLoadMoreCollections?: () => Promise<unknown>;
  collectionId: string | undefined | null;
  categoryId: string | undefined | null;
  onChange: (target: DraftFilingTarget) => void;
  /**
   * Whether a write is already in flight. Both fields are held while one is:
   * the category tree is scoped to `collectionId`, which only changes once the
   * re-file lands, so a pick made in between would write a category from the
   * collection being replaced — and the two updates would race besides.
   */
  isSaving?: boolean;
};

/**
 * Where a draft is filed, as the panel behind a breadcrumb's pencil.
 *
 * Each field writes as it changes, so there is nothing to confirm and no Save.
 * Shared by the review queue and the agent interface's AI draft modal, which
 * file the same drafts through different mutations — hence `onChange` rather
 * than a mutation of its own.
 */
export function DraftFiling({
  collections,
  collectionsLoading = false,
  hasMoreCollections = false,
  onLoadMoreCollections,
  collectionId,
  categoryId,
  onChange,
  isSaving = false,
}: DraftFilingProps): JSX.Element {
  const settling = collectionsLoading || isSaving;

  // `CategoryTreeInput` reads the title for this id out of the cache, which the
  // categories query has already filled.
  const category: ParentRef | undefined = categoryId
    ? { __typename: 'Category', id: categoryId }
    : undefined;

  return (
    <Box
      width="320px"
      padding="300"
      background="neutral.000"
      borderRadius="100"
    >
      <FlexBox flexDirection="column" gap="300">
        <FieldSelect
          label="Collection"
          required
          disabled={settling}
          placeholder="Select a collection"
          input={{
            name: 'draftFilingCollection',
            value: collectionId ? [collectionId] : [],
            onChange: (id?: string) => {
              // The select can clear, but a draft has to be in a collection.
              if (!id) return;
              // The category belongs to the old collection, so it goes with it.
              onChange({ collectionId: id, categoryId: null });
            },
          }}
          items={[...collections]}
          pagination={{
            infinite: true,
            nextEnable: hasMoreCollections,
            previousEnable: false,
            onNext: onLoadMoreCollections,
          }}
        />

        <CategoryTreeInput
          value={category}
          onChange={(parentRef) => onChange({ categoryId: parentRef.id })}
          withCollectionOption={false}
          currentCategoryId={undefined}
          collectionId={collectionId || undefined}
          required
          disabled={settling}
          label="Category"
          placeholder="Select a category"
        />
      </FlexBox>
    </Box>
  );
}
